import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AngularMaterialModule } from '../../../../angular-material.module';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '../../../../../environments/environment';

export interface EditRequestData {
  issue_id: number;
  user_id: number;
  role_id: number;
  issue_category_id: number;
  status: number;
  priority_id: number;
  assigned_to: number;
  description: string;
  estimated_cost: number;
  is_approved: boolean;
}

@Component({
  selector: 'app-edit-request',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AngularMaterialModule,
    CommonModule,
  ],
  templateUrl: './edit-request.component.html',
  styleUrls: ['./edit-request.component.scss'],
})
export class EditRequestComponent implements OnInit {
  statusList: any[] = [];
  categoryList: any[] = [];
  roleList: any[] = [];
  allMembers: any[] = [];
  priorityList: any[] = [];
  baseUrl = environment.baseUrl;
  accountID = sessionStorage.getItem('account_id');
  userID = sessionStorage.getItem('session_id');
  roleID = sessionStorage.getItem('role_id');
  requestData: any;
  originalIssueData: any; // Store original data from API for comparison
  loading = false;
  submitting = false;

  editRequestForm = new FormGroup({
    issue_id: new FormControl(this.data.issue_id),
    user_id: new FormControl(this.data.user_id),
    role_id: new FormControl(this.data.role_id),
    issue_category_id: new FormControl(this.data.issue_category_id, ),
    status: new FormControl(this.data.status, ),
    priority_id: new FormControl(this.data.priority_id, ),
    assigned_to: new FormControl(this.data.assigned_to, ),
    description: new FormControl(this.data.description, ),
    estimated_cost: new FormControl(this.data.estimated_cost ?? null),
    is_approved: new FormControl(this.data.is_approved ?? false),
  });

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) private data: any,
    private dialogRef: MatDialogRef<EditRequestComponent>
  ) {
    this.requestData = data;
  }

  ngOnInit(): void {
    if (this.data && this.data.item) {
      // Get account_id from item or session storage
      const itemAccountId = this.data.item.account_id || this.accountID;
      if (itemAccountId) {
        this.accountID = itemAccountId;
      }

      // Fetch dropdowns first
      this.fetchAllStatus();
      this.allCategoryDropdown();
      this.fetchAllPriority();
      this.fetchAllRoles();

      // Fetch single issue data
      this.fetchSingleIssue();

      // fetch members based on selected role
      this.editRequestForm.get('role_id')?.valueChanges.subscribe((roleId) => {
        if (roleId) {
          this.fetchStaffByRole(roleId);
        } else {
          this.allMembers = [];
        }
      });
    }
  }

  initializeForm(item: any) {
    this.editRequestForm.patchValue({
      issue_id: item.issue_id,
      user_id: item.user_id,
      role_id: item.assigned_to_role_id ?? item.role_id,
      issue_category_id: item.issue_category_id,
      status: item.status,
      priority_id: item.priority_id,
      assigned_to: item.assigned_to,
      description: item.description,
      estimated_cost: item.estimated_cost,
      is_approved: item.is_approved,
    });

    this.disableDefaultSelectedFields(item);

    const roleId = item.assigned_to_role_id ?? item.role_id;
    if (roleId) {
      this.fetchStaffByRole(roleId);
    }
  }

  private hasSelectedValue(value: unknown): boolean {
    if (value == null || value === '') {
      return false;
    }
    const num = Number(value);
    return !Number.isNaN(num) ? num > 0 : true;
  }

  private disableDefaultSelectedFields(item: any): void {
    const lockableFields = ['issue_category_id', 'role_id', 'assigned_to'] as const;
    lockableFields.forEach((field) =>
      this.editRequestForm.get(field)?.enable({ emitEvent: false })
    );

    if (this.hasSelectedValue(item.issue_category_id)) {
      this.editRequestForm.get('issue_category_id')?.disable({ emitEvent: false });
    }

    const roleId = item.assigned_to_role_id ?? item.role_id;
    if (this.hasSelectedValue(roleId)) {
      this.editRequestForm.get('role_id')?.disable({ emitEvent: false });
    }

    if (this.hasSelectedValue(item.assigned_to)) {
      this.editRequestForm.get('assigned_to')?.disable({ emitEvent: false });
    }
  }

  fetchAllStatus() {
    this.http.get(`${this.baseUrl}/status_dropdown`).subscribe({
      next: (res: any) => (this.statusList = res),
      error: (err: any) => this.handleError(err, 'Failed to fetch statuses'),
    });
  }

  allCategoryDropdown() {
    const accountGroupId = this.getAccountGroupId(this.accountID);
    if (accountGroupId == null) {
      this.categoryList = [];
      return;
    }
    this.http.post(`${this.baseUrl}/category_dropdown`, { account_group_id: accountGroupId }).subscribe({
      next: (res: any) => (this.categoryList = res),
      error: (err: any) => this.handleError(err, 'Failed to fetch categories'),
    });
  }

  private getAccountGroupId(accountId: string | number | null | undefined): number | null {
    const fromItem = this.data?.item?.account_group_id ?? this.originalIssueData?.account_group_id;
    if (fromItem != null && Number(fromItem) > 0) {
      return Number(fromItem);
    }

    const raw = sessionStorage.getItem('accounts');
    if (!raw || accountId == null) {
      return null;
    }

    try {
      const accounts = JSON.parse(raw);
      if (!Array.isArray(accounts)) {
        return null;
      }
      const match = accounts.find(
        (a: { account_id?: number | string; account_group_id?: number }) =>
          Number(a.account_id) === Number(accountId)
      );
      const groupId = match?.account_group_id;
      return groupId != null && Number(groupId) > 0 ? Number(groupId) : null;
    } catch {
      return null;
    }
  }

  fetchAllPriority() {
    this.http.get(`${this.baseUrl}/priority_dropdown`).subscribe({
      next: (res: any) => (this.priorityList = res),
      error: (err: any) => this.handleError(err, 'Failed to fetch priorities'),
    });
  }

  fetchAllRoles() {
    const payload = { account_id: this.accountID };
    this.http.get(`${this.baseUrl}/role_dropdown`).subscribe({
      next: (res: any) => (this.roleList = res),
      error: (err: any) => this.handleError(err, 'Failed to fetch roles'),
    });
  }

  fetchSingleIssue() {
    if (!this.data || !this.data.item) {
      return;
    }

    const item = this.data.item;
    const accountId = item.account_id || this.accountID;
    const issueId = item.issue_id;
    const userId = item.user_id || this.userID;

    if (!accountId || !issueId || !userId) {
      this.snackBar.open('Missing required data to fetch issue details.', 'Close', { duration: 3000 });
      return;
    }

    this.loading = true;
    const payload = {
      account_id: accountId,
      issue_id: issueId,
      user_id: userId,
    };

    this.http.post(`${this.baseUrl}/fetch_single_issue`, payload).subscribe({
      next: (res: any) => {
        // Store original data for comparison
        this.originalIssueData = res;
        this.requestData = { item: res };
        
        // Initialize form with fetched data
        this.initializeForm(res);
        
        // Refresh categories with account_group_id from fetched issue if available
        this.allCategoryDropdown();
      },
      error: (err: any) => {
        console.error('Error fetching single issue:', err);
        this.handleError(err, 'Failed to fetch issue details');
        // Fallback to using data.item if API fails
        this.initializeForm(this.data.item);
        this.originalIssueData = this.data.item;
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  fetchStaffByRole(roleId: any) {
    if (!roleId || !this.accountID) {
      this.allMembers = [];
      return;
    }

    const payload = {
      account_id: this.accountID,
      role_id: roleId,
    };

    this.http.post(`${this.baseUrl}/staff_dropdown`, payload).subscribe({
      next: (res: any) => {
        const staff = Array.isArray(res) ? res : (res?.data ?? []);
        this.allMembers = staff.map((m: any) => ({
          ...m,
          member_name: `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim(),
        }));
      },
      error: (err: any) => this.handleError(err, 'Failed to fetch members'),
    });
  }

  handleError(error: any, message: string) {
    console.error(message, error);
    this.snackBar.open(message, 'Close', { duration: 3000 });
  }

  closeDialog() {
    this.dialogRef.close(false);
  }

  onSubmit() {
  if (!this.editRequestForm.valid) {
    this.snackBar.open('Please fill all required fields correctly.', 'Close', { duration: 3000 });
    return;
  }

  const formValue = this.editRequestForm.getRawValue();
  const payload: { [key: string]: any } = {
    issue_id: formValue.issue_id,
    role_id: formValue.role_id,
    account_id: this.accountID,
    user_id: formValue.user_id,
    assigned_by: this.userID,
  };

  // List of editable fields to track
  const fieldsToCheck = [
    'issue_category_id',
    'status',
    'priority_id',
    'assigned_to',
    'description',
    'estimated_cost',
    'is_approved',
  ] as const;

  let isChanged = false;

  // Compare form values with original data and add to payload if changed
  const originalData = this.originalIssueData || this.data.item;
  fieldsToCheck.forEach((field) => {
    const key = field as keyof typeof payload;
    const newValue = formValue[field];
    const oldValue = originalData[field as keyof typeof originalData];

    payload[key] = newValue;

    if (newValue !== oldValue) {
      isChanged = true;
    }
  });

  if (!isChanged) {
    this.snackBar.open('No changes detected to save.', 'Close', { duration: 3000 });
    return;
  }

  // Submit payload to backend
  this.submitting = true;
  this.http.post(`${this.baseUrl}/assign_task`, payload).subscribe({
    next: (response) => {
      this.snackBar.open('Issue updated successfully!', 'Close', { duration: 3000 });
      this.dialogRef.close(response);
    },
    error: (err) => {
      console.error('Error updating issue:', err);
      this.snackBar.open('Failed to update issue, please try again later.', 'Close', { duration: 3000 });
    },
    complete: () => {
      this.submitting = false;
    },
  });
}

}
